import axios, { AxiosError, AxiosResponse } from "axios"
import {
	PolicyPayload,
	ApiResponse,
	ValidationResult,
	DownloadResponse,
	LicensePlateData,
	SavedPolicy,
	PaidPoliciesResponse,
	PolicyInsurerStatus,
	UserVehicle,
} from "../types"
import { authStorage } from "../utils/storage"
import { handleApiError, logError } from "../utils/errorHandler"

// Базовый URL для API (гарантируем корректный протокол и суффикс "/api")
// Если переменная окружения не задана, используем продакшен домен по умолчанию
const RAW_API_URL = process.env.REACT_APP_API_URL || "https://api.bluecards.ru"
const API_BASE_URL = RAW_API_URL.endsWith("/api")
	? RAW_API_URL
	: RAW_API_URL.replace(/\/+$/, "") + "/api"

// Создаем экземпляр axios
const api = axios.create({
	baseURL: API_BASE_URL,
	timeout: 30000, // 30 секунд таймаут для генерации PDF
})

// Отправляем cookies вместе с запросами к API
api.defaults.withCredentials = true

// Функция для получения актуального токена из in-memory storage
const getAccessToken = (): string | null => {
	return authStorage.getAccessToken()
}

// Функция для установки токена в in-memory storage
const setAccessToken = (token: string | null) => {
	if (token) {
		authStorage.setAccessToken(token)
	} else {
		authStorage.removeAccessToken()
	}
}

// Request interceptor - читаем актуальный токен из памяти
api.interceptors.request.use((config) => {
	const token = getAccessToken()
	if (token) {
		config.headers = config.headers || {}
		;(config.headers as any)["Authorization"] = `Bearer ${token}`
	}
	return config
})

// Авто-рефреш по 401 с использованием refreshToken из cookie
let isRefreshing = false
let failedQueue: Array<{
	resolve: (value?: any) => void
	reject: (reason?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error)
		} else {
			prom.resolve(token)
		}
	})
	failedQueue = []
}

api.interceptors.response.use(
	(r) => r,
	async (err: AxiosError) => {
		const original = err.config as any

		// Обрабатываем только 401 ошибки
		if (err.response?.status === 401 && !original?._retry) {
			// Если уже идет обновление токена, добавляем запрос в очередь
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject })
				})
					.then((token) => {
						original.headers["Authorization"] = `Bearer ${token}`
						return api(original)
					})
					.catch((e) => {
						return Promise.reject(e)
					})
			}

			original._retry = true
			isRefreshing = true

			try {
				// Пытаемся обновить токен используя refreshToken из httpOnly cookie
				// Backend читает cookie автоматически, возвращает только accessToken
				const res = await axios.post<{ accessToken: string }>(
					`${API_BASE_URL}/auth/refresh`,
					{}, // Пустое тело - refreshToken передаётся через cookie
					{
						withCredentials: true,
						headers: {
							"Content-Type": "application/json",
						},
					}
				)

				const newAccessToken = res.data.accessToken
				setAccessToken(newAccessToken)

				// Обновляем заголовок оригинального запроса
				original.headers["Authorization"] = `Bearer ${newAccessToken}`

				// Обрабатываем очередь успешных запросов
				processQueue(null, newAccessToken)

				isRefreshing = false

				// Повторяем оригинальный запрос
				return api(original)
			} catch (refreshError) {
				// Если refresh не удался, очищаем токены
				processQueue(refreshError, null)
				setAccessToken(null)
				authStorage.clearAuth()
				isRefreshing = false

				// Редирект только с защищённых страниц; главная, тарифы и auth — публичные
				const path = typeof window !== "undefined" ? window.location.pathname : ""
				const isPublicRoute =
					path === "/" ||
					path.startsWith("/auth/") ||
					path.startsWith("/tariffs") ||
					path.startsWith("/verify-") ||
					path.startsWith("/payment/")
				if (typeof window !== "undefined" && !isPublicRoute) {
					window.location.href = "/auth/login"
				}

				return Promise.reject(refreshError)
			}
		}

		return Promise.reject(err)
	}
)

// Сервис для работы с API
export class PdfGeneratorApi {
	/**
	 * Получение списка полисов пользователя
	 */
	static async getUserPolicies(): Promise<SavedPolicy[]> {
		try {
			const response: AxiosResponse<SavedPolicy[]> = await api.get("/policies")
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "PdfGeneratorApi.getUserPolicies")
			throw new Error(appError.message)
		}
	}

	/**
	 * Скачивание конкретного полиса
	 */
	static async downloadPolicy(policyId: string): Promise<DownloadResponse> {
		try {
			const response: AxiosResponse<Blob> = await api.get(
				`/policies/${policyId}/download`,
				{
					responseType: "blob",
				}
			)

			// Проверяем, что ответ действительно содержит данные
			if (
				!response.data ||
				(response.data instanceof Blob && response.data.size === 0)
			) {
				throw new Error("Получен пустой ответ от сервера")
			}

			// Убеждаемся, что blob имеет правильный MIME type
			let blob = response.data
			const contentType = response.headers["content-type"] || "application/pdf"

			if (!(blob instanceof Blob)) {
				blob = new Blob([blob], { type: contentType })
			} else if (blob.type !== contentType && contentType.includes("pdf")) {
				blob = new Blob([blob], { type: "application/pdf" })
			}

			// Извлекаем имя файла из заголовков ответа
			const contentDisposition = response.headers["content-disposition"]
			let filename = `policy_${policyId}.pdf`

			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename="(.+)"/)
				if (filenameMatch) {
					filename = filenameMatch[1]
				}
			}

			return {
				blob: blob,
				filename: filename,
			}
		} catch (error: any) {
			if (error.response?.data) {
				// Если ответ - это blob, пытаемся прочитать его как текст
				if (error.response.data instanceof Blob) {
					try {
						const errorText = await error.response.data.text()
						const errorData = JSON.parse(errorText)
						throw new Error(errorData.error || "Ошибка при скачивании полиса")
					} catch {
						throw new Error("Ошибка при скачивании полиса")
					}
				} else {
					const errorText = await error.response.data.text()
					try {
						const errorData = JSON.parse(errorText)
						throw new Error(errorData.error || "Ошибка при скачивании полиса")
					} catch {
						throw new Error("Ошибка при скачивании полиса")
					}
				}
			}
			throw new Error(error.message || "Ошибка при скачивании полиса")
		}
	}

	/**
	 * Получение метаданных полиса
	 */
	static async getPolicyMetadata(policyId: string): Promise<SavedPolicy> {
		try {
			const response: AxiosResponse<SavedPolicy> = await api.get(
				`/policies/${policyId}`
			)
			return response.data
		} catch (error: any) {
			throw new Error(
				error.response?.data?.error ||
					"Ошибка при получении информации о полисе"
			)
		}
	}

	/**
	 * Удаление полиса
	 */
	static async deletePolicy(policyId: string): Promise<{ message: string }> {
		try {
			const response: AxiosResponse<{ message: string }> = await api.delete(
				`/policies/${policyId}`
			)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "PdfGeneratorApi.deletePolicy")
			throw new Error(appError.message)
		}
	}

	/**
	 * Валидация даты
	 */
	static async validateDate(date: string): Promise<ValidationResult> {
		try {
			const response: AxiosResponse<ValidationResult> = await api.post(
				"/validate-date",
				{
					date: date,
				}
			)
			return response.data
		} catch (error: any) {
			return {
				valid: false,
				error: error.response?.data?.error || "Ошибка валидации даты",
			}
		}
	}

	/**
	 * Валидация регистрационного знака
	 */
	static async validateLicensePlate(
		plateData: LicensePlateData
	): Promise<ValidationResult> {
		try {
			const response: AxiosResponse<ValidationResult> = await api.post(
				"/validate-license-plate",
				{
					license_plate: plateData,
				}
			)
			return response.data
		} catch (error: any) {
			return {
				valid: false,
				error:
					error.response?.data?.error ||
					"Ошибка валидации регистрационного знака",
			}
		}
	}

	/**
	 * Проверка работоспособности API
	 */
	static async healthCheck(): Promise<ApiResponse> {
		try {
			const response: AxiosResponse<ApiResponse> = await api.get("/health")
			return response.data
		} catch (error: any) {
			return {
				error: error.message || "API недоступен",
			}
		}
	}
}

export class PaymentsApi {
	static async initPayment(payload: {
		policyData: PolicyPayload
	}): Promise<{
		widgetUrl: string
		redirectUrl: string
		invoiceId: string
		amount: number
	}> {
		const res = await api.post<{
			widgetUrl: string
			redirectUrl: string
			invoiceId: string
			amount: number
		}>("/payments/robokassa/init", payload)
		return res.data
	}

	static async getPaymentStatus(invoiceId: string): Promise<{
		status: string
		policyId?: string
	}> {
		const res = await api.get(`/payments/robokassa/status/${invoiceId}`)
		return res.data
	}

	static async getMyPayments(): Promise<any[]> {
		const res = await api.get("/payments/robokassa/my-payments")
		return res.data
	}
}

/**
 * Сервис для авторизации
 *
 * Архитектура токенов:
 * - accessToken: возвращается в JSON, хранится в памяти (защита от XSS)
 * - refreshToken: передаётся ТОЛЬКО через httpOnly cookie (не доступен JavaScript)
 */
export class AuthApi {
	/**
	 * Вход в систему
	 * Backend возвращает accessToken в JSON и устанавливает refreshToken в httpOnly cookie
	 */
	static async login(email: string, password: string): Promise<void> {
		const res = await api.post<{ accessToken: string }>(
			"/auth/login",
			{ email, password },
			{ withCredentials: true }
		)
		setAccessToken(res.data.accessToken)
	}

	/**
	 * Регистрация пользователя
	 * Backend возвращает accessToken в JSON и устанавливает refreshToken в httpOnly cookie
	 */
	static async register(
		name: string,
		email: string,
		password: string,
		phone: string
	): Promise<void> {
		const res = await api.post<{ accessToken: string }>(
			"/auth/register",
			{ name, email, password, phone },
			{ withCredentials: true }
		)
		setAccessToken(res.data.accessToken)
	}

	/**
	 * Выход из системы
	 */
	static async logout(): Promise<void> {
		await api.post("/auth/logout", {}, { withCredentials: true })
		setAccessToken(null)
		authStorage.clearAuth()
	}

	/**
	 * Получить текущего пользователя
	 */
	static async me(): Promise<{
		id: string
		name?: string
		email?: string
		phone?: string | null
		phoneVerified?: boolean
		phoneVerifiedAt?: string | null
		firstName?: string | null
		lastName?: string | null
		middleName?: string | null
		birthDate?: string | null
		address?: string | null
		role?: string
		emailVerified?: boolean
		emailVerifiedAt?: string | null
		// Транспорт пользователя (до 5 автомобилей)
		vehicles?: UserVehicle[]
	}> {
		const res = await api.get("/auth/@me", { withCredentials: true })
		return res.data
	}

	static async updateProfile(data: {
		name?: string
		email?: string
		phone?: string | null
		firstName?: string | null
		lastName?: string | null
		middleName?: string | null
		birthDate?: string | null
		address?: string | null
	}): Promise<{
		id: string
		name?: string
		email?: string
		phone?: string | null
		phoneVerified?: boolean
		phoneVerifiedAt?: string | null
		firstName?: string | null
		lastName?: string | null
		middleName?: string | null
		birthDate?: string | null
		address?: string | null
		role?: string
		emailVerified?: boolean
		emailVerifiedAt?: string | null
	}> {
		const res = await api.patch("/auth/@me", data, { withCredentials: true })
		return res.data
	}

	// --- Транспорт пользователя (профиль) ---

	static async getMyVehicles(): Promise<UserVehicle[]> {
		const res = await api.get("/auth/@me/vehicles")
		return res.data
	}

	static async addVehicle(payload: {
		regNumber: string
		vehicleType: string
		vehicleCategory?: string
		brandId: string
		modelId: string
		brandModel: string
		licensePlate?: LicensePlateData
		isPrimary?: boolean
	}): Promise<UserVehicle> {
		const res = await api.post("/auth/@me/vehicles", payload)
		return res.data
	}

	static async deleteVehicle(vehicleId: string): Promise<{ success: boolean }> {
		const res = await api.delete(`/auth/@me/vehicles/${vehicleId}`)
		return res.data
	}

	static async sendEmailVerification(): Promise<{ sent: boolean }> {
		const res = await api.post(
			"/auth/email/send-verification",
			{},
			{ withCredentials: true }
		)
		return res.data
	}

	static async confirmEmail(token: string): Promise<{ success: true }> {
		const res = await api.get("/auth/email/confirm", {
			params: { token },
			withCredentials: true,
		})
		return res.data
	}
}

/**
 * Подтверждение email (ссылка + резервный 4-значный код)
 */
export class EmailVerificationApi {
	static async send(): Promise<{ ok: boolean; alreadyVerified?: boolean }> {
		try {
			const res = await api.post<{ ok: boolean; alreadyVerified?: boolean }>(
				"/auth/email/send",
				{}
			)
			return res.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "EmailVerificationApi.send")
			throw new Error(appError.message || "Не удалось отправить письмо")
		}
	}

	static async verifyCode(code: string): Promise<{ ok: boolean }> {
		try {
			const res = await api.post<{ ok: boolean }>("/auth/email/verify-code", { code })
			return res.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "EmailVerificationApi.verifyCode")
			throw new Error(appError.message || "Не удалось подтвердить код")
		}
	}

	static async verifyToken(token: string): Promise<{ ok: boolean }> {
		try {
			const res = await api.post<{ ok: boolean }>("/auth/email/verify-token", { token })
			return res.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "EmailVerificationApi.verifyToken")
			throw new Error(appError.message || "Не удалось подтвердить email")
		}
	}
}

/**
 * Сервис для отправки писем (SMTP через backend)
 */
export class MailApi {
	/**
	 * Отправить тестовое письмо через backend
	 * По умолчанию backend отправит на email текущего пользователя.
	 */
	static async sendTestEmail(to?: string): Promise<{ ok: boolean }> {
		try {
			const response = await api.post<{ ok: boolean }>(
				"/mail/test",
				to ? { to } : {}
			)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "MailApi.sendTestEmail")
			throw new Error(appError.message || "Ошибка отправки письма")
		}
	}
}

/**
 * Сервис для работы с транспортными средствами
 */
export class VehiclesApi {
	/**
	 * Получение списка всех марок
	 */
	static async getBrands(): Promise<{
		brands: Array<{ id: string; name: string; cyrillic_name: string }>
	}> {
		try {
			const response = await api.get(`/vehicle-brands`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "VehiclesApi.getBrands")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получение моделей по марке
	 */
	static async getModels(brandId: string): Promise<{
		models: Array<{
			id: string
			name: string
			cyrillic_name: string
			full_name: string
		}>
	}> {
		try {
			const response = await api.get(`/vehicle-models/${brandId}`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "VehiclesApi.getModels")
			throw new Error(appError.message)
		}
	}

	/**
	 * Поиск моделей автомобилей
	 */
	static async searchVehicles(
		query: string
	): Promise<{ results: Array<{ label: string; value: string }> }> {
		try {
			const response = await api.get(`/search-vehicles`, {
				params: { q: query },
			})
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "VehiclesApi.searchVehicles")
			throw new Error(appError.message)
		}
	}
}

/**
 * Сервис для работы с тарифами
 */
export class TariffsApi {
	/**
	 * Получение всех тарифов
	 */
	static async getAllTariffs(): Promise<any[]> {
		try {
			const response = await api.get("/tariffs")
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "TariffsApi.getAllTariffs")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получение тарифа по ID
	 */
	static async getTariffById(id: string): Promise<any> {
		try {
			const response = await api.get(`/tariffs/${id}`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "TariffsApi.getTariffById")
			throw new Error(appError.message)
		}
	}

	/**
	 * Поиск тарифа по типу транспорта
	 */
	static async searchTariff(vehicleType: string): Promise<any> {
		try {
			// Маппинг кодов типов ТС на названия в БД
			const vehicleTypeMapping: Record<string, string> = {
				A: "Legkovye avtomobili",
				B: "Motocikly, motorollery, motokoljaska i mopedy",
				C: "Gruzovye avtomobili, tyagachi",
				D: "Motocikly, motorollery, motokoljaska i mopedy",
				E: "Avtobusy",
				F1: "Pricepy k legkovym avtomobilyam",
				F2: "Pricepy i polupricepy k gruzovym avtomobilyam i tyagacham",
				// G. OTHERS / ПРОЧИЕ — используем тариф
				// "Сельскохозяйственная и строительная техника"
				G: "Selskokhozyajstvennaya i stroitelnaya tekhnika",
			}

			const mappedVehicleType = vehicleTypeMapping[vehicleType] || vehicleType

			const response = await api.get("/tariffs/search", {
				params: { vehicleType: mappedVehicleType },
			})
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "TariffsApi.searchTariff")
			throw new Error(appError.message)
		}
	}

	/**
	 * Обновление тарифа (только для админов)
	 */
	static async updateTariff(id: string, data: any): Promise<any> {
		try {
			const response = await api.patch(`/tariffs/${id}`, data)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "TariffsApi.updateTariff")
			throw new Error(appError.message)
		}
	}

	/**
	 * Удаление тарифа (только для админов)
	 */
	static async deleteTariff(id: string): Promise<void> {
		try {
			await api.delete(`/tariffs/${id}`)
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "TariffsApi.deleteTariff")
			throw new Error(appError.message)
		}
	}
}

/**
 * Сервис для работы с верификацией телефона (i-Digital Direct Widget)
 */
export class VerificationApi {
	/**
	 * Отправка кода верификации
	 * Backend должен проксировать запрос к i-Digital Direct API
	 */
	static async sendVerificationCode(
		key: string
	): Promise<{ messageUuid: string }> {
		try {
			const response = await api.post<{ messageUuid: string }>(
				"/verification/send",
				{ key }
			)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "VerificationApi.sendVerificationCode")
			throw new Error(appError.message || "Ошибка отправки кода верификации")
		}
	}

	/**
	 * Проверка верификации телефона
	 * Backend должен проксировать запрос к i-Digital Direct API
	 */
	static async checkVerification(key: string): Promise<{
		status: string
		destination: string
		confirmedAt: string
		firstSuccessfulCheck: boolean
	}> {
		try {
			const response = await api.post<{
				status: string
				destination: string
				confirmedAt: string
				firstSuccessfulCheck: boolean
			}>("/verification/check", { key })
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "VerificationApi.checkVerification")
			throw new Error(appError.message || "Ошибка проверки верификации")
		}
	}
}

/**
 * Сервис для админ-панели (только для ADMIN)
 */
export class AdminApi {
	/**
	 * Получение списка пользователей с пагинацией
	 */
	static async getUsers(params?: {
		page?: number
		limit?: number
		search?: string
		sortBy?: string
		sortOrder?: "asc" | "desc"
	}): Promise<{
		users: Array<{
			id: string
			name: string
			email: string
			emailVerified: boolean
			phone: string | null
			phoneVerified: boolean
			role: string
			hasPolicies: boolean
			policiesCount: number
			createdAt: string
		}>
		total: number
		page: number
		limit: number
		totalPages: number
	}> {
		try {
			const response = await api.get("/admin/users", { params })
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.getUsers")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получение детальной информации о пользователе
	 */
	static async getUserDetails(userId: string): Promise<{
		id: string
		name: string
		email: string
		emailVerified: boolean
		phone: string | null
		phoneVerified: boolean
		phoneVerifiedAt: string | null
		role: string
		createdAt: string
		updatedAt: string
		passportVerified: boolean
		policies: Array<{
			id: string
			fio: string
			vehicleType: string
			brandModel: string
			regNumber: string
			dateStart: string
			dateEnd: string
			status: string
			amount: number
			createdAt: string
			s3Url: string | null
		}>
		paymentsCount: number
		totalPaid: number
	}> {
		try {
			const response = await api.get(`/admin/users/${userId}`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.getUserDetails")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получение статистики для дашборда
	 */
	static async getStats(): Promise<{
		totalUsers: number
		totalPolicies: number
		totalRevenue: number
		usersToday: number
		policiesToday: number
	}> {
		try {
			const response = await api.get("/admin/stats")
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.getStats")
			throw new Error(appError.message)
		}
	}

	static async getPaidPolicies(params?: {
		page?: number
		limit?: number
		search?: string
		dateFrom?: string
		dateTo?: string
	}): Promise<PaidPoliciesResponse> {
		try {
			const response = await api.get("/admin/paid-policies", { params })
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.getPaidPolicies")
			throw new Error(appError.message)
		}
	}

	static async updatePolicyInsurerStatus(
		policyId: string,
		payload: { sentToInsurer?: boolean; confirmedByInsurer?: boolean }
	): Promise<PolicyInsurerStatus> {
		try {
			const response = await api.patch(
				`/admin/policies/${policyId}/insurer-status`,
				payload
			)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.updatePolicyInsurerStatus")
			throw new Error(appError.message)
		}
	}

	static async deletePolicy(policyId: string): Promise<{ message: string }> {
		try {
			const response = await api.delete(`/admin/policies/${policyId}`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.deletePolicy")
			throw new Error(appError.message)
		}
	}

	static async deleteUser(userId: string): Promise<{ message: string }> {
		try {
			const response = await api.delete(`/admin/users/${userId}`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.deleteUser")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получение presigned URL PDF полиса для просмотра
	 * URL действителен ограниченное время (expiresIn секунд)
	 */
	static async getPolicyPdfUrl(policyId: string): Promise<{
		url: string | null
		fileName: string
		expiresIn: number
	}> {
		try {
			const response = await api.get(`/admin/policies/${policyId}/pdf-url`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.getPolicyPdfUrl")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получение списка сессий пользователя
	 */
	static async getUserSessions(userId: string): Promise<
		Array<{
			id: string
			deviceInfo: string | null
			lastUsedAt: string
			createdAt: string
			expiresAt: string
			isExpired: boolean
		}>
	> {
		try {
			const response = await api.get(`/admin/users/${userId}/sessions`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.getUserSessions")
			throw new Error(appError.message)
		}
	}

	/**
	 * Отозвать конкретную сессию пользователя
	 */
	static async revokeUserSession(
		userId: string,
		sessionId: string
	): Promise<{ message: string }> {
		try {
			const response = await api.delete(
				`/admin/users/${userId}/sessions/${sessionId}`
			)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.revokeUserSession")
			throw new Error(appError.message)
		}
	}

	/**
	 * Отозвать все сессии пользователя
	 */
	static async revokeAllUserSessions(userId: string): Promise<{
		message: string
	}> {
		try {
			const response = await api.delete(`/admin/users/${userId}/sessions`)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "AdminApi.revokeAllUserSessions")
			throw new Error(appError.message)
		}
	}
}

/**
 * API для проверки паспорта РФ
 */
export interface PassportVerificationResult {
	success: boolean
	status: "PENDING" | "VALID" | "INVALID" | "NOT_FOUND" | "ERROR"
	message: string
	requestUuid?: string
	verifiedAt?: string
}

export interface PassportVerificationStatus {
	verified: boolean
	verifiedAt: string | null
	lastCheck: PassportVerificationResult | null
}

export class PassportVerificationApi {
	/**
	 * Проверить паспорт РФ через базу СМЭВ
	 */
	static async verify(data: {
		surname: string
		name: string
		passport_series: string
		passport_number: string
	}): Promise<PassportVerificationResult> {
		try {
			const response = await api.post("/passport-verification/verify", data)
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "PassportVerificationApi.verify")
			throw new Error(appError.message)
		}
	}

	/**
	 * Получить статус проверки паспорта для текущего пользователя
	 */
	static async getStatus(): Promise<PassportVerificationStatus> {
		try {
			const response = await api.get("/passport-verification/status")
			return response.data
		} catch (error: any) {
			const appError = handleApiError(error)
			logError(error, "PassportVerificationApi.getStatus")
			throw new Error(appError.message)
		}
	}
}

/**
 * Утилита для скачивания файла
 */
export const downloadFile = (blob: Blob, filename: string): void => {
	const url = window.URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	window.URL.revokeObjectURL(url)
}
